import { useRouter } from "next/navigation";
import { useCallback } from "react";
import {
  bugsPath,
  companyPath,
  ideasPath,
  projectsPath,
  quickWinsPath,
  roadmapPath,
} from "@/app/paths";
import { projectKindBug, projectKindIdea, projectKindQuickWin } from "@/app/util/projectsUtil";

// Navigation constants
const RETURN_TO_KEY = "returnTo";
export const RETURN_TO_ROADMAP = "roadmap" as const;
export const RETURN_TO_HOME_NEW = "home-new" as const;
export type ReturnToValue = typeof RETURN_TO_ROADMAP | typeof RETURN_TO_HOME_NEW;

/**
 * Hook for handling back navigation from project pages
 * Checks sessionStorage for explicit return route, otherwise uses the configured fallback destination.
 */
export function useProjectBackNavigation(
  companyId: number | string,
  projectKind?: string,
  options: {fallback?: "projectKind" | "projects"} = {},
) {
  const router = useRouter();
  const fallback = options.fallback ?? "projectKind";

  const navigateBack = useCallback(() => {
    // Check for explicit return route in sessionStorage
    if (typeof window !== "undefined") {
      const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
      if (returnTo === RETURN_TO_ROADMAP) {
        sessionStorage.removeItem(RETURN_TO_KEY);
        router.push(roadmapPath(companyId));
        return;
      }
      if (returnTo === RETURN_TO_HOME_NEW) {
        sessionStorage.removeItem(RETURN_TO_KEY);
        router.push(companyPath(companyId));
        return;
      }
    }

    if (fallback === "projects") {
      router.push(projectsPath(companyId));
      return;
    }

    // Default navigation based on project kind
    if (projectKind === projectKindIdea) {
      router.push(ideasPath(companyId));
    } else if (projectKind === projectKindQuickWin) {
      router.push(quickWinsPath(companyId));
    } else if (projectKind === projectKindBug) {
      router.push(bugsPath(companyId));
    } else {
      router.push(projectsPath(companyId));
    }
  }, [companyId, fallback, projectKind, router]);

  return { navigateBack };
}

/**
 * Helper function to mark that the user should return to roadmap
 * Call this when navigating from roadmap to a project
 */
export function setReturnToRoadmap() {
  if (typeof window !== "undefined") {
    sessionStorage.setItem(RETURN_TO_KEY, RETURN_TO_ROADMAP);
  }
}

/**
 * Helper function to mark that the user should return to the new home page
 * Call this when navigating from home-new to a project/idea/quick-win
 */
export function setReturnToHomeNew() {
  if (typeof window !== "undefined") {
    sessionStorage.setItem(RETURN_TO_KEY, RETURN_TO_HOME_NEW);
  }
}
